/**
 * When a spoken ANSWER is over — «منشی هوشمند»'s follow-up questions (PM-0080).
 *
 * An answer is a few words («۰۹۱۳…», «تأمین اجتماعی»), and making the desk press
 * «توقف و اجرا» after each one is what made the secretary feel like a form. So an
 * answer's recording ends by itself: speech, then SILENCE_MS of quiet. And one that
 * never hears speech gives up after NO_SPEECH_MS — cancelled, never sent, so a
 * question nobody answered by voice costs nothing.
 *
 * `createSilenceDetector` is the rule, pure: fed one microphone level (RMS, 0..1) at
 * a time with the time it was read. `watchSilence` feeds it from a live stream.
 */

export const SILENCE_MS = 1200;
export const NO_SPEECH_MS = 10000;

/**
 * The rule. The first few readings set the room's noise floor; speech is anything
 * `ratio` times louder than that (and never quieter than `minLevel`).
 * Returns 'silence' or 'no-speech' once — the reading that decided — else null.
 */
export function createSilenceDetector({
  silenceMs = SILENCE_MS, noSpeechMs = NO_SPEECH_MS, minLevel = 0.012, ratio = 2.5, floorReadings = 4,
} = {}) {
  let startedAt = null;
  const floorSeen = [];
  let floor = null;
  let spoke = false;
  let quietSince = null;
  let decided = false;

  return function feed(level, now) {
    if (decided) return null;
    if (startedAt === null) startedAt = now;
    if (floor === null) {
      // Speech can start at once: a loud first reading is speech, not the floor.
      if (level >= minLevel * 3) spoke = true;
      else floorSeen.push(level);
      if (floorSeen.length >= floorReadings || spoke) {
        floor = floorSeen.length ? floorSeen.reduce((a, b) => a + b, 0) / floorSeen.length : 0;
      }
      if (!spoke) return null;
    }
    const threshold = Math.max(minLevel, floor * ratio);
    if (level >= threshold) {
      spoke = true;
      quietSince = null;
      return null;
    }
    if (spoke) {
      if (quietSince === null) quietSince = now;
      if (now - quietSince >= silenceMs) { decided = true; return 'silence'; }
      return null;
    }
    if (now - startedAt >= noSpeechMs) { decided = true; return 'no-speech'; }
    return null;
  };
}

/**
 * Feed the rule from a live microphone stream, ten readings a second. `onLevel`
 * hears every reading (the record button's meter); `detect: false` reads the
 * level without the rule — a command's recording ends on its press, not on silence.
 *
 * `ready` resolves to whether the browser actually lets us listen: an AudioContext
 * that stays `suspended` (iOS Safari, when the context was not created by a tap)
 * reads nothing but zeros, and a detector fed zeros would call every answer
 * «no speech». The caller decides what to do without it.
 */
export function watchSilence(stream, { onSilence, onNoSpeech, onLevel, detect = true, ...rule } = {}) {
  const Ctx = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
  if (!Ctx || !stream) return { stop() {}, ready: Promise.resolve(false) };
  let ctx;
  let source;
  try {
    ctx = new Ctx();
    source = ctx.createMediaStreamSource(stream);
  } catch {
    try { ctx?.close(); } catch { /* ignore */ }
    return { stop() {}, ready: Promise.resolve(false) };
  }
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  source.connect(analyser);
  const samples = new Float32Array(analyser.fftSize);
  const feed = createSilenceDetector(rule);

  let timer = null;
  let stopped = false;
  // Once: closing a closed AudioContext REJECTS (it does not throw), and the hook's
  // teardown may stop a watch that already stopped itself on silence.
  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (timer) { clearInterval(timer); timer = null; }
    try { source.disconnect(); } catch { /* ignore */ }
    try { Promise.resolve(ctx.close()).catch(() => {}); } catch { /* ignore */ }
  };
  timer = setInterval(() => {
    if (ctx.state !== 'running') return;
    analyser.getFloatTimeDomainData(samples);
    let sum = 0;
    for (let i = 0; i < samples.length; i += 1) sum += samples[i] * samples[i];
    const level = Math.sqrt(sum / samples.length);
    onLevel?.(level);
    if (!detect) return;
    const event = feed(level, Date.now());
    if (event === 'silence') { stop(); onSilence?.(); }
    else if (event === 'no-speech') { stop(); onNoSpeech?.(); }
  }, 100);

  const ready = (async () => {
    try { await ctx.resume?.(); } catch { /* ignore */ }
    await new Promise((resolve) => { setTimeout(resolve, 300); });
    return ctx.state === 'running';
  })();
  return { stop, ready };
}
